import React, { useState, useCallback, useMemo } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  ConnectionMode,
  MarkerType,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';

import { GraphNode, GraphEdge, GraphData, NodeKind, ALLOWED_CONNECTIONS, NODE_STYLES, EDGE_STYLES } from './types';
import CustomNode from './CustomNode';
import EdgeWizard from './EdgeWizard';
import { relationshipsApi } from './api';
import { useAuthStore } from '../../store/authStore';

const nodeTypes = {
  custom: CustomNode
};

type LayoutMode = 'all-ikus' | 'iku-focus' | 'twin-chain';
type CanvasLayoutAlgorithm = 'polar' | 'force' | 'grid';

interface CanvasProps {
  data?: GraphData;
  layoutMode: LayoutMode;
  selectedIku?: string | null;
  onNodeSelect?: (node: GraphNode) => void;
  onEdgeSelect?: (edge: GraphEdge) => void;
}

function layoutNodesGrid(nodes: GraphNode[]): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();
  const cols = Math.ceil(Math.sqrt(nodes.length));
  nodes.forEach((node, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    positions.set(node.id, { x: 200 + col * 220, y: 100 + row * 140 });
  });
  return positions;
}

function layoutNodesForce(nodes: GraphNode[], edges: GraphEdge[]): Map<string, { x: number; y: number }> {
  // Simple spring layout: start from polar, then apply basic attraction/repulsion
  // Using a deterministic seed-based placement grouped by kind
  const positions = new Map<string, { x: number; y: number }>();
  const kindGroups = new Map<string, GraphNode[]>();
  nodes.forEach(n => {
    if (!kindGroups.has(n.kind)) kindGroups.set(n.kind, []);
    kindGroups.get(n.kind)!.push(n);
  });
  const kinds = Array.from(kindGroups.keys());
  const cx = 600, cy = 450;
  kinds.forEach((kind, ki) => {
    const groupNodes = kindGroups.get(kind)!;
    const groupAngle = (2 * Math.PI * ki) / kinds.length - Math.PI / 2;
    const groupRadius = 300;
    const gx = cx + groupRadius * Math.cos(groupAngle);
    const gy = cy + groupRadius * Math.sin(groupAngle);
    const innerR = Math.min(80, groupNodes.length * 20);
    groupNodes.forEach((node, ni) => {
      const a = (2 * Math.PI * ni) / Math.max(groupNodes.length, 1);
      positions.set(node.id, { x: gx + innerR * Math.cos(a), y: gy + innerR * Math.sin(a) });
    });
  });
  return positions;
}

const TWIN_COLUMN_X: Record<string, number> = {
  product: 80,
  legal_entity: 380,
  asset: 680,
  device: 980,
};

function layoutTwinChain(nodes: GraphNode[]): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();
  const byRole = new Map<string, GraphNode[]>();
  nodes.forEach((node) => {
    const role = String(node.props?.twin_role || '');
    if (!byRole.has(role)) byRole.set(role, []);
    byRole.get(role)!.push(node);
  });
  ['product', 'legal_entity', 'asset', 'device'].forEach((role) => {
    const column = byRole.get(role) || [];
    column.forEach((node, index) => {
      positions.set(node.id, {
        x: TWIN_COLUMN_X[role] ?? 80,
        y: 160 + index * 160,
      });
    });
  });
  nodes.forEach((node) => {
    if (!positions.has(node.id)) {
      positions.set(node.id, { x: 80, y: 160 });
    }
  });
  return positions;
}

function layoutNodes(
  nodes: GraphNode[],
  edges: GraphEdge[],
  mode: LayoutMode,
  selectedIku?: string | null,
  algorithm: CanvasLayoutAlgorithm = 'polar'
): Map<string, { x: number; y: number }> {
  if (mode === 'twin-chain') return layoutTwinChain(nodes);
  if (algorithm === 'grid') return layoutNodesGrid(nodes);
  if (algorithm === 'force') return layoutNodesForce(nodes, edges);

  const positions = new Map<string, { x: number; y: number }>();

  if (mode === 'all-ikus') {
    const ikuNodes = nodes.filter(n => n.kind === 'IKUCategory');
    const cx = 500, cy = 400, radius = 350;
    ikuNodes.forEach((node, i) => {
      const angle = (2 * Math.PI * i) / ikuNodes.length - Math.PI / 2;
      positions.set(node.id, {
        x: cx + radius * Math.cos(angle),
        y: cy + radius * Math.sin(angle),
      });
    });
    return positions;
  }

  // iku-focus mode: selected IKU at center, templates around it, connected IKUs on outer ring
  const ikuFullId = selectedIku ? `iku:${selectedIku}` : null;
  const centerNode = nodes.find(n => n.id === ikuFullId);
  const templates = nodes.filter(n => n.kind === 'Template');
  const connectedIkus = nodes.filter(n => n.kind === 'IKUCategory' && n.id !== ikuFullId);

  const cx = 600, cy = 450;

  if (centerNode) {
    positions.set(centerNode.id, { x: cx, y: cy });
  }

  // Lay out templates in a semicircle or full circle depending on count
  if (templates.length > 0) {
    const innerRadius = templates.length <= 3 ? 220 : templates.length <= 8 ? 300 : 380;
    templates.forEach((node, i) => {
      const angle = (2 * Math.PI * i) / templates.length - Math.PI / 2;
      positions.set(node.id, {
        x: cx + innerRadius * Math.cos(angle),
        y: cy + innerRadius * Math.sin(angle),
      });
    });

    // Connected IKUs on outer ring
    const outerRadius = (templates.length <= 3 ? 220 : templates.length <= 8 ? 300 : 380) + 280;
    connectedIkus.forEach((node, i) => {
      const angle = (2 * Math.PI * i) / Math.max(connectedIkus.length, 1) - Math.PI / 2;
      positions.set(node.id, {
        x: cx + outerRadius * Math.cos(angle),
        y: cy + outerRadius * Math.sin(angle),
      });
    });
  } else {
    // No templates — just show connected IKUs around center
    const radius = 300;
    connectedIkus.forEach((node, i) => {
      const angle = (2 * Math.PI * i) / Math.max(connectedIkus.length, 1) - Math.PI / 2;
      positions.set(node.id, {
        x: cx + radius * Math.cos(angle),
        y: cy + radius * Math.sin(angle),
      });
    });
  }

  return positions;
}

const EDGE_CATEGORY_COLORS: Record<string, string> = {
  static: '#6366F1',
  operational: '#F59E0B',
  event: '#EF4444',
};

function convertToReactFlowData(
  graphData: GraphData,
  mode: LayoutMode,
  selectedIku?: string | null,
  algorithm: CanvasLayoutAlgorithm = 'polar'
) {
  const positions = layoutNodes(graphData.nodes, graphData.edges, mode, selectedIku, algorithm);

  const nodes: Node[] = graphData.nodes.map((node) => ({
    id: node.id,
    type: 'custom',
    position: positions.get(node.id) || { x: 0, y: 0 },
    data: {
      ...node,
      style: NODE_STYLES[node.kind] || NODE_STYLES.Template,
    },
  }));

  const edges: Edge[] = graphData.edges.map(edge => {
    const category = edge.props?.category || '';
    const color = EDGE_CATEGORY_COLORS[category] || '#94A3B8';
    const isRelTypeDef = edge.type === 'relationship_type_definition';
    const isBelongsTo = edge.type === 'belongs_to_iku';
    const isSuperseded =
      Boolean(edge.props?._ui_superseded) ||
      String(edge.props?.status || '').toLowerCase() === 'superseded';
    const baseLabel = isBelongsTo
      ? ''
      : edge.props?.rel_name || edge.type?.replace('rel:', '').replace(/_/g, ' ') || edge.type;
    const label = isSuperseded && baseLabel ? `${baseLabel} · superseded` : isSuperseded ? 'superseded' : baseLabel;

    return {
      id: edge.id,
      source: edge.from,
      target: edge.to,
      type: 'smoothstep',
      animated: !isRelTypeDef && !isBelongsTo && !isSuperseded,
      style: {
        stroke: isSuperseded ? '#D97706' : isBelongsTo ? '#D1D5DB' : color,
        strokeWidth: isRelTypeDef ? 1.5 : isBelongsTo ? 1 : 2,
        opacity: isSuperseded ? 0.55 : isBelongsTo ? 0.4 : isRelTypeDef ? 0.6 : 0.9,
        strokeDasharray: isSuperseded ? '6 4' : undefined,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: isSuperseded ? '#D97706' : isBelongsTo ? '#D1D5DB' : color,
        width: 16,
        height: 16,
      },
      label,
      labelStyle: {
        fontSize: 9,
        fill: isSuperseded ? '#B45309' : '#6B7280',
        fontWeight: isSuperseded ? 600 : 500,
      },
      labelBgStyle: { fill: isSuperseded ? '#FFFBEB' : '#ffffff', fillOpacity: 0.9 },
      labelBgPadding: [4, 2] as [number, number],
      data: edge,
    };
  });

  return { nodes, edges };
}

export default function Canvas({ data: externalData, layoutMode, selectedIku, onNodeSelect, onEdgeSelect }: CanvasProps) {
  const reactFlowInstance = useReactFlow();
  const { token, selectedTenant } = useAuthStore();
  const [showEdgeWizard, setShowEdgeWizard] = useState(false);
  const [pendingConnection, setPendingConnection] = useState<Connection | null>(null);
  const [edgeSaving, setEdgeSaving] = useState(false);
  const [edgeError, setEdgeError] = useState<string | null>(null);
  const [layoutAlgorithm, setLayoutAlgorithm] = useState<CanvasLayoutAlgorithm>('polar');

  const graphData = externalData;

  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    if (!graphData) return { nodes: [], edges: [] };
    return convertToReactFlowData(graphData, layoutMode, selectedIku, layoutAlgorithm);
  }, [graphData, layoutMode, selectedIku, layoutAlgorithm]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  React.useEffect(() => {
    if (graphData) {
      const { nodes: newNodes, edges: newEdges } = convertToReactFlowData(graphData, layoutMode, selectedIku, layoutAlgorithm);
      setNodes(newNodes);
      setEdges(newEdges);
    }
  }, [graphData, layoutMode, selectedIku, layoutAlgorithm, setNodes, setEdges]);

  const onConnect = useCallback((connection: Connection) => {
    if (!connection.source || !connection.target) return;
    const sourceNode = nodes.find(n => n.id === connection.source);
    const targetNode = nodes.find(n => n.id === connection.target);
    if (!sourceNode || !targetNode) return;

    const sourceKind = sourceNode.data.kind as NodeKind;
    const targetKind = targetNode.data.kind as NodeKind;
    const allowedConnections = ALLOWED_CONNECTIONS[sourceKind] || [];
    const validConnection = allowedConnections.find(conn => conn.target === targetKind);

    if (!validConnection) return;

    setPendingConnection({
      ...connection,
      data: { edgeType: validConnection.edgeType, sourceKind, targetKind },
    });
    setShowEdgeWizard(true);
  }, [nodes]);

  const handleEdgeWizardComplete = useCallback(async (edgeData: any) => {
    if (!pendingConnection || !token || !selectedTenant) return;
    setEdgeSaving(true);
    setEdgeError(null);
    try {
      const result = await relationshipsApi.createEdge(
        pendingConnection.source!,
        pendingConnection.target!,
        edgeData.type,
        edgeData.props || {},
        token,
        selectedTenant
      );
      const serverId = result?.edge?.id || result?.id || `edge:${pendingConnection.source}->${pendingConnection.target}`;
      const newEdge: Edge = {
        id: serverId,
        source: pendingConnection.source!,
        target: pendingConnection.target!,
        type: 'smoothstep',
        animated: true,
        style: { stroke: '#F59E0B', strokeDasharray: '5 5', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#F59E0B', width: 16, height: 16 },
        label: edgeData.type,
        data: { ...edgeData, status: 'observed' },
      };
      setEdges(eds => addEdge(newEdge, eds));
      setShowEdgeWizard(false);
      setPendingConnection(null);
    } catch (err: any) {
      setEdgeError(err?.message || 'Failed to save relationship. Please try again.');
    } finally {
      setEdgeSaving(false);
    }
  }, [pendingConnection, setEdges, token, selectedTenant]);

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    onNodeSelect?.(node.data as GraphNode);
  }, [onNodeSelect]);

  const onEdgeClick = useCallback((_: React.MouseEvent, edge: Edge) => {
    onEdgeSelect?.(edge.data as GraphEdge);
  }, [onEdgeSelect]);

  if (!graphData || graphData.nodes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-gray-400">
          <div className="text-lg font-medium">No relationships to display</div>
          <div className="text-sm mt-1">
            {layoutMode === 'twin-chain'
              ? 'Honest empty — Product, Legal Entity, Asset, or Device is not linked yet.'
              : 'Select an IKU category or wait for data to load'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        nodeTypes={nodeTypes}
        connectionMode={ConnectionMode.Loose}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        className="bg-gray-50"
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: false,
        }}
      >
        <Background gap={20} size={1} color="#e5e7eb" />
        <Controls />
        <MiniMap
          nodeColor={(node) => node.data?.style?.bgColor || '#e5e7eb'}
          maskColor="rgb(240, 240, 240, 0.6)"
          position="bottom-right"
        />
      </ReactFlow>

      {/* Layout Toggle Toolbar */}
      <div className="absolute top-4 right-4 bg-white/95 backdrop-blur rounded-lg shadow-sm border border-gray-200 p-1 flex gap-1 text-xs">
        {(['polar', 'force', 'grid'] as CanvasLayoutAlgorithm[]).map(alg => (
          <button
            key={alg}
            onClick={() => setLayoutAlgorithm(alg)}
            className={`px-2.5 py-1 rounded transition-colors font-medium ${
              layoutAlgorithm === alg
                ? 'bg-indigo-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            title={alg === 'polar' ? 'Circular layout' : alg === 'force' ? 'Group-based layout' : 'Grid layout'}
          >
            {alg === 'polar' ? 'Polar' : alg === 'force' ? 'Force' : 'Grid'}
          </button>
        ))}
      </div>

      {showEdgeWizard && pendingConnection && (
        <EdgeWizard
          connection={pendingConnection}
          onComplete={handleEdgeWizardComplete}
          onCancel={() => { setShowEdgeWizard(false); setPendingConnection(null); setEdgeError(null); }}
          saving={edgeSaving}
          saveError={edgeError}
        />
      )}

      {/* Edge save error toast */}
      {edgeError && !showEdgeWizard && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white text-xs px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2">
          <span>⚠️ {edgeError}</span>
          <button onClick={() => setEdgeError(null)} className="ml-2 opacity-70 hover:opacity-100">✕</button>
        </div>
      )}

      {/* Legend */}
      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-lg shadow-sm border border-gray-200 p-3 text-xs">
        <div className="font-medium text-gray-700 mb-2">Relationship Types</div>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="w-5 h-0.5 rounded" style={{ backgroundColor: '#6366F1' }} />
            <span className="text-gray-600">Static (permanent)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-0.5 rounded" style={{ backgroundColor: '#F59E0B' }} />
            <span className="text-gray-600">Operational (can change)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-0.5 rounded" style={{ backgroundColor: '#EF4444' }} />
            <span className="text-gray-600">Event (point in time)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-0.5 rounded" style={{ backgroundColor: '#94A3B8' }} />
            <span className="text-gray-600">Type definition</span>
          </div>
        </div>
      </div>
    </div>
  );
}
